import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useState } from 'react';
import NeueHochschuleDaten from 'src/components/auth/abteilung-registrieren/hochschule-daten/NeueHochschuleDaten';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { APIS } from 'src/lib/constants/apis';
import { FORM_FIELDS_TYPES } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { getFieldNamefromDataPath, getOptionsFromRes, isTrue } from 'src/utils/helper';

// import schemas
import HochschuleSchema from 'src/lib/paths/hochschule_FULL_Paths.json';
import OrganisationsSchema from 'src/lib/paths/organisation_FULL_Paths.json';
const O_P = OrganisationsSchema.attributes;
const HS_P = HochschuleSchema.attributes;

const AbteilungsDaten: FC<{
	formValue: any;
	updateFunction: (_key: string, _value: any) => void;
	organisationDatenError: any;
	hochschulen: any[];
	neueHochschuleDaten: any;
	updateNeueHochschuleFunction: (_key: string, _value: any) => void;
	neueHochschuleDatenError: any;
	onNeueHochschuleAnlegen: () => boolean;
}> = ({
	formValue,
	updateFunction,
	organisationDatenError,
	hochschulen,
	neueHochschuleDaten,
	updateNeueHochschuleFunction,
	neueHochschuleDatenError,
	onNeueHochschuleAnlegen,
}) => {
	const [open, setOpen] = useState(false);

	const handleAnlegen = () => {
		const isValid = onNeueHochschuleAnlegen();
		if (isValid) {
			setOpen(false);
		}
	};

	return (
		<>
			<ApplicationHeading title={'Organisationsprofils '} />
			<InhaltWrapper marginTop='0'>
				<Typography variant='body1'>
					Bitte geben Sie hier die ersten Informationen zu Ihrer Organisation an.{' '}
					<strong>
						Prüfen Sie bitte zunächst, ob Ihre Organisation in der Auswahlliste “Hochschule / Forschungseinrichtung” aufgeführt ist. Falls nicht
						- klicken Sie auf den Button "Hochschule / Forschungseinrichtung hinzufügen".
					</strong>
				</Typography>
				<Typography variant='body1'>
					Die Stammdaten aus dem Organisationsprofil werden bei der Erstellung eines Antrags immer automatisch übernommen. Bitte gehen Sie daher
					nach erfolgter Registrierung erneut in Ihr Organisationsprofil und vervollständigen Sie dort die Angaben um weitere Informationen.
				</Typography>
			</InhaltWrapper>
			<Stack sx={{ mt: '1rem' }} justifyContent={'end'} alignItems={'flex-end'}>
				<Button
					sx={{ width: 'max-content', maxWidth: 'none', whiteSpace: 'nowrap' }}
					endIcon={Object.keys(neueHochschuleDaten).length > 0 ? <EditIcon /> : <AddIcon />}
					onClick={() => setOpen(true)}
				>
					{Object.keys(neueHochschuleDaten).length > 0 ? 'Hochschule bearbeiten' : 'Hochschule / Forschungseinrichtung hinzufügen'}
				</Button>
			</Stack>

			<Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					Neue Hochschule / Forschungseinrichtung hinzufügen
					<IconButton onClick={() => setOpen(false)} aria-label='Schließen'>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 1 }}>
						<NeueHochschuleDaten
							formValues={neueHochschuleDaten}
							updateFormValuesFunc={updateNeueHochschuleFunction}
							formErrors={neueHochschuleDatenError}
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button variant='outlined' onClick={() => setOpen(false)}>
						Abbrechen
					</Button>
					<Button variant='contained' onClick={handleAnlegen}>
						Anlegen
					</Button>
				</DialogActions>
			</Dialog>

			<InhaltTrenner />

			<CreateForm
				formValues={formValue}
				formStruc={formStrucOrga(formValue, hochschulen)}
				updateFormValuesFunc={updateFunction}
				formErrors={organisationDatenError}
				api={APIS.Organisation}
			/>
		</>
	);
};

export default AbteilungsDaten;

const formStrucOrga = (orgaDaten: any, hochschulen: any[]) => {
	const hochSchulenOptions = getOptionsFromRes(hochschulen, getFieldNamefromDataPath(HS_P.Geschaeftsbezogene_Daten_angezeigter_Name.path));
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...O_P.Hochschule,
					type: FORM_FIELDS_TYPES.singleSelect,
					options: hochSchulenOptions,
					label: 'Hessische Hochschulen',
				},
				{ ...O_P.Geschaeftsbezogene_Daten_Name_Der_Organisation, label: 'Fachbereich/Abteilung' },
				{ ...O_P.Adressdaten_Strasse_Und_Hausnummer },
				{ ...O_P.Adressdaten_Adresszusatz },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [{ ...O_P.Adressdaten_PLZ }, { ...O_P.Adressdaten_Ort }],
			userProps: { columnToDivide: 2, columnGap: '1rem' },
		},

		{
			fields: [{ ...O_P.Adressdaten_Abweichender_Sitz }],
			userProps: { columnToDivide: 2, columnGap: '1rem' },
		},
	];

	const abweichenderSitzValue = get(orgaDaten, O_P.Adressdaten_Abweichender_Sitz.path, false);

	if (isTrue(abweichenderSitzValue)) {
		struc.push(
			{
				fields: [{ ...O_P.Adressdaten_Abweichender_Sitz_Adresse_Strasse_Und_Hausnummer }],
				userProps: { columnToDivide: 1, columnGap: '1rem' },
			},
			{
				fields: [{ ...O_P.Adressdaten_Abweichender_Sitz_Adresse_PLZ }, { ...O_P.Adressdaten_Abweichender_Sitz_Adresse_Ort }],
				userProps: { columnToDivide: 2, columnGap: '1rem' },
			},
		);
	}
	return struc;
};
